import { Mutation, JsonApiRequestError } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { sample, Store } from 'effector';
import {
	USER_NAME_MAX_LENGTH,
	USER_NAME_MIN_LENGTH,
	USER_NOTE_MAX_LENGTH,
	USER_PASSWORD_MIN_LENGTH,
} from 'entities/user';
import {
	createEditUserMutation,
	EditUserMutationParams,
	User,
	UserRole,
	UserWithPermission,
} from 'shared/api';
import {
	createModalFormModel,
	emptyOrMinLengthRule,
	maxLengthRule,
	minLengthRule,
	ModalFormModel,
	notEmptyRule,
	notify,
} from 'shared/lib';

export type EditUserFormValue = {
	name: string;
	password: string;
	role: UserRole | null;
	note: string;
	hasMultiLogin: boolean;
};

export type EditUserModel = ModalFormModel<UserWithPermission, EditUserFormValue> & {
	editUserMutation: Mutation<EditUserMutationParams, User, JsonApiRequestError>;
	$pending: Store<boolean>;
};

export const createEditUserModel = (): EditUserModel => {
	const editUserMutation = createEditUserMutation();

	const model = createModalFormModel<UserWithPermission, EditUserFormValue>({
		fields: {
			name: {
				init: '',
				rules: [
					notEmptyRule,
					minLengthRule(USER_NAME_MIN_LENGTH),
					maxLengthRule(USER_NAME_MAX_LENGTH),
				],
			},
			password: {
				init: '',
				rules: [emptyOrMinLengthRule(USER_PASSWORD_MIN_LENGTH)],
			},
			role: {
				init: null,
				rules: [notEmptyRule],
			},
			note: {
				init: '',
				rules: [maxLengthRule(USER_NOTE_MAX_LENGTH)],
			},
			hasMultiLogin: {
				init: false,
			},
		},
		validateOn: ['submit'],
		resetErrorsOnChange: true,
	});

	const { $pending } = editUserMutation;

	sample({
		clock: model.modalButtonClicked,
		fn: (user) => ({
			name: user.name,
			password: '',
			role: user.role,
			note: user.note ?? '',
			hasMultiLogin: user.hasMultiLogin,
		}),
		target: [model.form.setValues, model.form.setInitialValues],
	});

	sample({
		clock: model.form.submitted,
		source: { values: model.form.$values, user: model.$modalData },
		fn: ({ values, user }) => ({
			id: user!.id,
			name: values.name,
			password: values.password || undefined,
			role: values.role!,
			note: values.note || undefined,
			hasMultiLogin: values.hasMultiLogin,
		}),
		target: editUserMutation.start,
	});

	chain(editUserMutation.finished.success, model.switcher.disable);

	notify(editUserMutation.finished.success, 'edit-user.success');

	chain(model.modalClosed, editUserMutation.reset);

	return {
		editUserMutation,
		$pending,
		...model,
	};
};
